import type { SelectProps } from 'antd'
import type { ArrayElement } from 'type-fest'

export type SelectOptions = SelectProps['options']
export type SelectOption = ArrayElement<SelectOptions>
export type SelectOptionsInput = SelectOptions | string[] | undefined

export function isLabelInValueLike(value: unknown): value is { value: unknown; label?: unknown; key?: unknown } {
  if (!value || typeof value !== 'object') {
    return false
  }

  const record = value as Record<string, unknown>
  return 'value' in record && ('label' in record || 'key' in record)
}

export function unwrapSelectValue(value: unknown): unknown {
  if (isLabelInValueLike(value)) {
    return value.value
  }

  return value
}

export function normalizeOptions(options: SelectOptionsInput): SelectOptions | undefined {
  if (!options) return undefined

  const normalized: SelectOption[] = []
  for (const option of options) {
    if (typeof option === 'string') {
      normalized.push({ value: option, label: option } as SelectOption)
      continue
    }

    normalized.push(option)
  }

  return normalized as SelectOptions
}

export function findOptionByValue(options: SelectOptions | undefined, targetValue: unknown): SelectOption | undefined {
  if (!options) return undefined

  for (const option of options) {
    if (!option || typeof option !== 'object') continue

    const optionRecord = option as Record<string, unknown>
    if ('value' in optionRecord && optionRecord.value === targetValue) {
      return option
    }

    if ('options' in optionRecord && Array.isArray(optionRecord.options)) {
      const nested = findOptionByValue(optionRecord.options as SelectOptions, targetValue)
      if (nested) return nested
    }

    if ('children' in optionRecord && Array.isArray(optionRecord.children)) {
      const nested = findOptionByValue(optionRecord.children as SelectOptions, targetValue)
      if (nested) return nested
    }
  }

  return undefined
}
